import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type ArrivalStepKind,
  type UpsertArrivalStepRequest,
} from '@/shared/api';
import { arrivalProblem } from './arrivalProblem';
import { GUIDE_COPY } from './guideCopy';

/**
 * Objekt-Ebene pflegt die gemeinsame Schrittfolge, Apartment-Ebene ergänzt und überschreibt.
 * `guide` sagt, **welche** Sammlung — Anfahrt oder Parkmöglichkeiten. Ohne Angabe die Anfahrt.
 */
export type ArrivalScope = { kind: 'object' | 'apartment'; id: string; guide?: ArrivalStepKind };

/**
 * Bündelt die beiden Endpoint-Familien hinter einer Oberfläche. Der Editor kennt nur „Schritt
 * speichern" — ob daraus am Apartment eine Überschreibung wird, entscheidet der Server.
 */
export function useArrivalGuideApi(scope: ArrivalScope) {
  const api = useApi();
  const qc = useQueryClient();
  const isObject = scope.kind === 'object';
  const guide: ArrivalStepKind = scope.guide ?? 'Arrival';
  // Die Art gehört in den Query-Key: sonst zeigt der Parkplätze-Reiter die Anfahrtsschritte aus
  // dem Zwischenspeicher des Nachbarreiters — und schreibt beim Speichern in die falsche Liste.
  const queryKey = isObject
    ? ['property-groups', scope.id, 'arrival-steps', guide]
    : ['properties', scope.id, 'arrival-steps', guide];

  const query = useQuery({
    queryKey,
    queryFn: () =>
      isObject
        ? api.arrivalSteps.listForObject(scope.id, guide)
        : api.arrivalSteps.listForApartment(scope.id, guide),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });
  // Nach einem Fehler kann die Liste überholt sein — ohne Antwort ist offen, was geschah.
  const onError = async (error: unknown) => {
    toast.error(arrivalProblem(await toApiError(error)));
    void invalidate();
  };

  const save = useMutation({
    mutationFn: ({ stepId, body }: { stepId?: string; body: UpsertArrivalStepRequest }) => {
      if (isObject) {
        return stepId
          ? api.arrivalSteps.updateForObject(scope.id, stepId, body, guide)
          : api.arrivalSteps.createForObject(scope.id, body, guide);
      }
      return stepId
        ? api.arrivalSteps.updateForApartment(scope.id, stepId, body, guide)
        : api.arrivalSteps.createForApartment(scope.id, body, guide);
    },
    // Den Grund zeigt das Sheet, das dabei offen bleibt; ein Toast verschwände mit ihm.
    networkMode: 'always',
    onSuccess: () => {
      toast.success('Gespeichert');
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (stepId: string) =>
      isObject
        ? api.arrivalSteps.deleteForObject(scope.id, stepId, guide)
        : api.arrivalSteps.deleteForApartment(scope.id, stepId, guide),
    networkMode: 'always',
    onSuccess: (result) => {
      const removed = result && 'removedOverrides' in result ? result.removedOverrides : 0;
      const deleted = `${GUIDE_COPY[guide].noun} gelöscht`;
      toast.success(
        removed > 0
          ? `${deleted} — ${removed} Apartment-Fassung${removed === 1 ? '' : 'en'} entfielen mit`
          : deleted,
      );
      void invalidate();
    },
    onError,
  });

  const move = useMutation({
    mutationFn: ({ stepId, direction }: { stepId: string; direction: 'Up' | 'Down' }) =>
      isObject
        ? api.arrivalSteps.moveForObject(scope.id, stepId, direction, guide)
        : api.arrivalSteps.moveForApartment(scope.id, stepId, direction, guide),
    networkMode: 'always',
    onSuccess: () => void invalidate(),
    onError,
  });

  const resetOverride = useMutation({
    mutationFn: (stepId: string) => api.arrivalSteps.resetOverride(scope.id, stepId, guide),
    networkMode: 'always',
    onSuccess: () => {
      toast.success('Überschreibung zurückgenommen');
      void invalidate();
    },
    onError,
  });

  return { query, save, remove, move, resetOverride, refresh: invalidate };
}
